import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Checkbox,
  Chip,
  Spinner,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { RetailTargetSku } from 'common/src/utils/types';
import { createManualRetailPrice } from 'common/src/api/retail_skus';
import {
  RETAIL_UNIT_OPTIONS,
  convertToKg,
  computeUsdPerKg,
  fetchRetailFxRate,
} from 'common/src/utils/retailPriceCalc';
import { useConfirm } from '../../../components/ui';

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

// ISO-2 origins we see most for fresh produce. Free of obligation —
// operator leaves "—" if not visible.
const ORIGIN_OPTIONS = [
  { key: '', label: '— Sin especificar —' },
  { key: 'CL', label: 'Chile (CL)' },
  { key: 'PE', label: 'Perú (PE)' },
  { key: 'MX', label: 'México (MX)' },
  { key: 'NZ', label: 'Nueva Zelanda (NZ)' },
  { key: 'IT', label: 'Italia (IT)' },
  { key: 'ES', label: 'España (ES)' },
  { key: 'US', label: 'Estados Unidos (US)' },
  { key: 'BR', label: 'Brasil (BR)' },
  { key: 'AR', label: 'Argentina (AR)' },
  { key: 'ZA', label: 'Sudáfrica (ZA)' },
];

// Soft sanity bounds for USD/kg — outside this we ask for confirmation.
const USD_KG_MIN = 0.1;
const USD_KG_MAX = 50;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sku: RetailTargetSku | null;
  onSaved: () => void;
}

const formatNum = (v: number | null | undefined, digits = 2) =>
  v == null
    ? '—'
    : v.toLocaleString(undefined, { maximumFractionDigits: digits });

const ManualPriceModal: React.FC<Props> = ({
  isOpen,
  onClose,
  sku,
  onSaved,
}) => {
  const [price, setPrice] = useState('');
  const confirm = useConfirm();
  const [sizeValue, setSizeValue] = useState('');
  const [sizeUnit, setSizeUnit] = useState('kg');
  const [origin, setOrigin] = useState('');
  const [onPromo, setOnPromo] = useState(false);
  const [saving, setSaving] = useState(false);

  // FX is fetched once when the modal opens for the SKU's currency.
  const [fx, setFx] = useState<{ rate: number; isFallback: boolean } | null>(
    null
  );
  const [fxLoading, setFxLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !sku) return;
    // reset form
    setPrice('');
    setSizeValue('');
    setSizeUnit('kg');
    setOrigin('');
    setOnPromo(false);
    setFx(null);

    setFxLoading(true);
    fetchRetailFxRate(sku.currency)
      .then((r) => setFx(r))
      .catch(() => setFx({ rate: 1, isFallback: true }))
      .finally(() => setFxLoading(false));
  }, [isOpen, sku]);

  const priceNum = parseFloat(price);
  const sizeNum = parseFloat(sizeValue);

  // Live preview of the computed conversion.
  const preview = useMemo(() => {
    if (!sku) return null;
    if (!Number.isFinite(priceNum) || !Number.isFinite(sizeNum)) return null;
    const conv = convertToKg(sizeNum, sizeUnit, sku.species_id);
    const usdKg = computeUsdPerKg(
      priceNum,
      sku.currency,
      conv.weight_kg,
      fx?.rate ?? null
    );
    return { ...conv, usdKg };
  }, [sku, priceNum, sizeNum, sizeUnit, fx]);

  if (!sku) return null;

  const priceValid = Number.isFinite(priceNum) && priceNum > 0;
  const sizeValid = Number.isFinite(sizeNum) && sizeNum > 0;
  const canSave = priceValid && sizeValid && !fxLoading && !saving;

  const usdKgOutOfRange =
    preview?.usdKg != null &&
    (preview.usdKg < USD_KG_MIN || preview.usdKg > USD_KG_MAX);

  const noWeight = preview != null && preview.weight_kg == null;

  const handleSave = async () => {
    if (!canSave) return;

    if (noWeight) {
      const ok = await confirm({
        title: '¿Guardar sin USD/kg?',
        message: `La unidad "${sizeUnit}" no tiene regla de conversión a kg para esta especie. Se guardará el precio pero sin USD/kg.`,
        confirmLabel: 'Guardar',
      });
      if (!ok) return;
    } else if (usdKgOutOfRange) {
      const ok = await confirm({
        title: '¿Precio fuera del rango habitual?',
        message: `El precio calculado es $${formatNum(preview?.usdKg, 2)} USD/kg, fuera del rango habitual ($${USD_KG_MIN}–$${USD_KG_MAX}). Revisa que el precio y la unidad sean correctos.`,
        confirmLabel: 'Guardar igual',
      });
      if (!ok) return;
    }

    setSaving(true);
    try {
      await createManualRetailPrice({
        sku,
        priceLocal: priceNum,
        packageSizeValue: sizeNum,
        packageSizeUnit: sizeUnit,
        packageSizeText: `${sizeNum} ${sizeUnit}`,
        weightKg: preview?.weight_kg ?? null,
        weightSource: preview?.weight_source ?? null,
        priceUsdPerKg: preview?.usdKg ?? null,
        fxRateUsed: fx?.rate ?? null,
        originCountry: origin || null,
        onPromo,
      });
      toast.success('Precio manual guardado');
      onSaved();
      onClose();
    } catch (e: unknown) {
      toast.error(`Error guardando: ${getErrorMessage(e)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='lg' scrollBehavior='inside'>
      <ModalContent>
        <ModalHeader className='flex flex-col gap-1'>
          <div className='flex items-center gap-2'>
            <Icon icon='tabler:keyboard' className='text-primary-500' />
            <span>Agregar precio manual</span>
            <Chip size='sm' variant='flat'>
              Manual
            </Chip>
          </div>
          <p className='text-sm font-normal text-default-500'>
            {sku.retailer} · {sku.country} · {sku.product_name}
          </p>
        </ModalHeader>

        <ModalBody>
          <div className='space-y-4'>
            <div className='rounded-lg bg-default-50 border border-default-200 px-3 py-2 text-sm text-default-600'>
              Ingresa solo el <strong>precio</strong> y la{' '}
              <strong>cantidad</strong> que ves en la página. El resto se
              calcula automáticamente.
            </div>

            <div className='grid grid-cols-2 gap-3'>
              <Input
                autoFocus
                size='sm'
                type='number'
                label={`Precio (${sku.currency})`}
                placeholder='0.00'
                value={price}
                onValueChange={setPrice}
                isInvalid={price !== '' && !priceValid}
                errorMessage={
                  price !== '' && !priceValid ? 'Debe ser mayor a 0' : undefined
                }
                startContent={
                  <span className='text-default-400 text-sm'>$</span>
                }
              />
              <div className='grid grid-cols-2 gap-2'>
                <Input
                  size='sm'
                  type='number'
                  label='Cantidad'
                  placeholder='1'
                  value={sizeValue}
                  onValueChange={setSizeValue}
                  isInvalid={sizeValue !== '' && !sizeValid}
                />
                <Select
                  size='sm'
                  label='Unidad'
                  disallowEmptySelection
                  selectedKeys={new Set([sizeUnit])}
                  onSelectionChange={(keys) => {
                    const k = Array.from(keys)[0];
                    if (k) setSizeUnit(String(k));
                  }}
                >
                  {RETAIL_UNIT_OPTIONS.map((o) => (
                    <SelectItem key={o.key} value={o.key}>
                      {o.label}
                    </SelectItem>
                  ))}
                </Select>
              </div>
            </div>

            <div className='grid grid-cols-2 gap-3 items-end'>
              <Select
                size='sm'
                label='Origen (opcional)'
                selectedKeys={new Set([origin])}
                onSelectionChange={(keys) => {
                  const k = Array.from(keys)[0];
                  setOrigin(k ? String(k) : '');
                }}
              >
                {ORIGIN_OPTIONS.map((o) => (
                  <SelectItem key={o.key} value={o.key}>
                    {o.label}
                  </SelectItem>
                ))}
              </Select>
              <Checkbox
                size='sm'
                isSelected={onPromo}
                onValueChange={setOnPromo}
                className='pb-2'
              >
                En promoción
              </Checkbox>
            </div>

            {/* Live preview */}
            <div className='rounded-lg border border-primary-200 bg-primary-50/40 dark:bg-primary-950/20 px-4 py-3'>
              <p className='text-xs font-semibold uppercase text-primary-600 mb-2'>
                Preview (calculado automáticamente)
              </p>
              {fxLoading ? (
                <div className='flex items-center gap-2 text-sm text-default-500'>
                  <Spinner size='sm' /> Cargando tipo de cambio...
                </div>
              ) : (
                <div className='grid grid-cols-3 gap-3 text-sm'>
                  <div>
                    <p className='text-default-500 text-xs'>Peso normalizado</p>
                    <p className='font-semibold'>
                      {preview?.weight_kg != null
                        ? `${formatNum(preview.weight_kg, 4)} kg`
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <p className='text-default-500 text-xs'>USD / kg</p>
                    <p
                      className={`font-semibold ${
                        usdKgOutOfRange ? 'text-warning-600' : ''
                      }`}
                    >
                      {preview?.usdKg != null
                        ? `$${formatNum(preview.usdKg, 2)}`
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <p className='text-default-500 text-xs'>
                      Dólar usado{fx?.isFallback ? ' (aprox)' : ''}
                    </p>
                    <p className='font-semibold'>
                      {fx ? `${formatNum(fx.rate, 4)} ${sku.currency}` : '—'}
                    </p>
                  </div>
                </div>
              )}
              {noWeight && (
                <p className='mt-2 text-xs text-warning-600'>
                  ⚠️ La unidad seleccionada no convierte a kg para esta especie —
                  se guardará sin USD/kg.
                </p>
              )}
              {usdKgOutOfRange && !noWeight && (
                <p className='mt-2 text-xs text-warning-600'>
                  ⚠️ El USD/kg está fuera del rango habitual ($
                  {USD_KG_MIN}–${USD_KG_MAX}). Revisa precio y unidad.
                </p>
              )}
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button variant='light' onClick={onClose} isDisabled={saving}>
            Cancelar
          </Button>
          <Button
            color='primary'
            onClick={handleSave}
            isLoading={saving}
            isDisabled={!canSave}
          >
            Guardar punto
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ManualPriceModal;
